import { Injectable, computed, inject, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { StateService } from './state.service';
import { firstValueFrom } from 'rxjs';

import { accountingUrl } from 'src/environments/environment';

/**
 * Accounting activation state — mirror of PayrollActivationService.
 *
 * Phases 1-2: server-authoritative pricing, a persisted plan selection, and the
 * entitlement summary that replaced the browser-side Bitrix lookup.
 * Payment lands in Phase 3 (there is no provisioning step: a paid — or free —
 * plan is active immediately)
 * (docs/plans/accounting-activation-flow-plan.md).
 */

export type AccountingPlan = 'diy' | 'bookkeeping';
export type DiyTier = 'free' | 'standard';
export type ClosingFrequency = 'monthly' | 'bimonthly' | 'quarterly' | 'halfyearly' | 'yearly';
export type ReviewFrequency = 'quarterly' | 'halfyearly' | 'yearly';

/**
 * Selection KEYS only — the server derives every amount from these.
 *
 * `account_review_freq` and `einvoice_band` are REQUESTS: the server clamps a
 * review cadence more frequent than book closing, and clamps an e-invoice band
 * below the floor implied by turnover.
 */
export interface AccountingSoftwareChoice {
  value: string;
  label: string;
  included: boolean;
  /** Offered as "Altomate subscribes for you" — included, or priced. */
  subscribe: boolean;
  annual_fee: number | null;
  /** Offered as "my own software". */
  own: boolean;
  needs_name: boolean;
}

export interface AccountingSelectionKeys {
  plan: AccountingPlan;
  tier?: DiyTier | null;
  closing_frequency?: ClosingFrequency | null;
  turnover_band?: string | null;
  addon_account_review?: boolean;
  account_review_freq?: ReviewFrequency | null;
  addon_einvoice?: boolean;
  /** Statutory SST FILING add-on — not the 8% service tax. */
  addon_sst_submission?: boolean;
  setup_enabled?: boolean;
  /** FY END month, 1-12. Collected at activation so no report ever silently
   *  defaults to a December financial year. */
  year_end?: number | null;
  /** Service window (bookkeeping only). Omitted = the 12-month default.
   *  The server re-derives the term length from these; the client never sends
   *  a cycle count. */
  term_start?: string | null;
  term_end?: string | null;
  /** The client's confirmation that a window running past 12 months is their
   *  FIRST financial period after incorporation (s.340 CA 2016 allows up to 18
   *  months to the first AGM). Only ever required for a single yearly bill
   *  covering more than 12 months; the server refuses one without it. */
  first_financial_period?: boolean;
  /** Which accounting package the client's books are kept in. BOOKKEEPING
   *  ONLY and required there — DIY *is* Altomate Accounting, so the server
   *  leaves it NULL on that plan. 'other' is a CS-only value, never sent from
   *  here. */
  accounting_software?: string | null;
  /** v2.33: true = Altomate holds the licence (annual fee on cycle 1);
   *  false = the client's own. Bookkeeping only. */
  software_subscribed_by_us?: boolean | null;
  /** The typed name behind `accounting_software = 'other'`. */
  accounting_software_name?: string | null;
}

/** Result of a save. Flat rather than a discriminated union — this project
 *  compiles with `strict: false`, where literal-type narrowing is unreliable. */
export interface AccountingSaveResult {
  ok: boolean;
  pricing?: AccountingPricingResult;
  invoice?: AccountingInvoiceRef | null;
  message?: string;
  code?: string;
}

/** Server-derived pricing. Never construct this on the client. */
export interface AccountingPricingResult extends AccountingSelectionKeys {
  account_review_freq: ReviewFrequency | null;
  /** Review cadences legal for this closing cadence (must divide it evenly). */
  allowed_review_frequencies: ReviewFrequency[];
  einvoice_band: string | null;
  requires_consultation: boolean;
  billing_interval_months: number;
  service_term_months: number;
  plan_period_amount: number | null;
  plan_periods_per_year: number;
  plan_amount: number | null;
  /** v2.33: annual third-party licence fee on cycle 1, when Altomate subscribes. */
  software_amount?: number | null;
  /** Charged this cycle (0 on cycles where the review is not due). */
  account_review_amount: number | null;
  /** Price of one review, regardless of whether it falls due this cycle. */
  account_review_period_amount: number | null;
  review_due_this_cycle: boolean;
  /** Review falls due every Nth billing cycle. Null when the add-on is off. */
  review_every_n_cycles: number | null;
  einvoice_monthly_amount: number | null;
  einvoice_amount: number | null;
  /** SST submission add-on. Named apart from `sst` (the 8% tax) on purpose. */
  addon_sst_submission?: boolean;
  sst_submission_period_amount?: number | null;
  sst_submission_amount?: number | null;
  sst_submission_every_n_cycles?: number | null;
  setup_amount: number;
  subtotal: number | null;
  sst: number | null;
  total: number | null;
  /** The engagement is a fixed term; instalments run inside it. */
  term_months: number;
  cycles_per_term: number;
  /** What the whole term costs across all instalments. */
  term_total: number | null;
  /** Alias of term_total, kept for display code. */
  annual_equivalent: number | null;
  lines: Array<{
    code: string; description: string;
    quantity: number; unit_amount: number; amount: number;
  }>;
  /** 'custom' = priced by CS from a quote, not from the published matrix. */
  pricing_source?: 'matrix' | 'custom';
}

/** No provisioning step: paid (or free) means active immediately. */
export type AccountingStatus =
  'none' | 'draft' | 'active' | 'suspended' | 'expired';

/** Where a company should be sent next, derived from server state only. */
export type AccountingStage = 'home' | 'plan' | 'payment' | 'profile' | 'dashboard';

export interface AccountingTerm {
  id: number;
  term_no: number;
  term_start: string;
  term_end: string;
  status: string;
  billing_interval_months: number;
  cycles_per_term: number;
  term_total: number | null;
}

/** Server-computed entitlement. The ONLY source of truth for accounting access. */
export interface AccountingSummary {
  organisation_id: number;
  activation_status: AccountingStatus;
  /** Has this company ever been activated — the access gate. */
  can_enter?: boolean;
  /** Last date a record may be dated (term end + grace). null = no ceiling. */
  coverage_until?: string | null;
  /** How many months of that are grace past the term end. */
  coverage_grace_months?: number;
  /** Show a renewal prompt. A PROMPT — never a wall. */
  renewal_due?: boolean;
  plan: AccountingPlan | null;
  /** UF_CRM_1759198255445 — free/paid TIER: '6328' free, '6329' paid. */
  bitrix_plan_code: string | null;
  /** UF_CRM_1628220067640 — the PRODUCT: '6213' DIY Free, '6324' DIY Standard, '2361' Bookkeeping. */
  bitrix_service_type: string | null;
  tier: DiyTier | null;
  source: string | null;
  valid_from: string | null;
  valid_until: string | null;
  payment_status: 'none' | 'pending' | 'paid' | 'void';
  latest_invoice: AccountingInvoiceRef | null;
  /** Submitted a bank transfer or an existing-client declaration and waiting on
   *  CS. Distinct from "never paid" — the invoice is pending in both cases. */
  manual_review: boolean;
  manual_review_kind: 'bank_transfer' | 'existing_client' | null;
  manual_review_at: string | null;
  term: AccountingTerm | null;
  selection: any | null;
  billing_mode: 'standard' | 'offline' | 'offline_suspended';
  /** FY END month 1-12 from company settings, or null when never set. The
   *  renewal screen SHOWS this rather than asking for it again. */
  year_end?: number | null;
  setup_steps: { accounting_profile: boolean };
  /** A plan change asked for and not yet in effect. null when none. */
  pending_change?: AccountingPlanChange | null;
}

/**
 * A change the client asked for, held apart from the live plan until it takes
 * effect (server: a_subscription_plan_change). Phase 1 knows one kind.
 */
/** One priced item on an invoice, as stored on its payload for Xero. */
export interface AccountingInvoiceLine {
  code: string | null;
  description: string;
  quantity: number;
  unit_amount: number;
  amount: number;
}

export interface AccountingPlanChange {
  id: number;
  kind: 'downgrade_free' | 'upgrade_standard' | 'upgrade_bookkeeping';
  /** Downgrade: the day the tier flips. */
  effective_at: string | null;
  invoice_id: number | null;
  custom_quote_id: number | null;
  keep_review: boolean;
  /** An upgrade's first-month invoice. `under_review` = a transfer was declared
   *  and CS is confirming it — no Pay button, no Cancel.
   *
   *  `lines` is the invoice's own itemisation, read off its stored payload —
   *  the same lines that go to Xero — so a CS-quoted upgrade shows its
   *  workings instead of a bare total. Empty on invoices raised before this
   *  was carried; the total still renders. */
  invoice?: { id: number; total: number; currency: string; due_date: string | null;
    status: 'pending' | 'paid' | 'void'; under_review: boolean;
    lines?: AccountingInvoiceLine[];
    subtotal?: number | null; sst?: number | null;
    /** 'custom' = priced by CS off a quote rather than the published matrix. */
    pricing_source?: string | null;
    xero_quote_number?: string | null;
    cycles_per_term?: number | null;
    term_months?: number | null;
    billing_interval_months?: number | null } | null;
}

/** Everything the Airwallex drop-in needs, minted server-side. */
export interface AccountingIntent {
  success: boolean;
  invoiceId: number | null;
  intentId?: string;
  clientSecret: string;
  customerId?: string;
  amount: number;
  currency: string;
  countryCode: string;
  /** Which Airwallex environment the SDK must initialise in to match the secret. */
  env: 'demo' | 'prod';
  alreadyPaid?: boolean;
  message?: string;
}

export interface AccountingInvoiceRef {
  id: number;
  status: 'pending' | 'paid' | 'void';
  subtotal: number;
  sst: number;
  total: number;
  currency: string;
}

interface SaveResponse {
  success: boolean;
  requires_consultation?: boolean;
  selection?: AccountingPricingResult;
  invoice?: AccountingInvoiceRef | null;
  message?: string;
  code?: string;
}

/**
 * One instalment of a term, as `a_subscription_charge` actually stores it.
 *
 * Not derived on the client, ever. `cycle_no` is STORED server-side precisely so
 * a late or retried charge does not shift the schedule, and re-deriving a
 * calendar here from `term_start` would reintroduce the drift that column exists
 * to prevent.
 */
export interface AccountingCharge {
  cycle_no: number;
  period_start: string | null;
  period_end: string | null;
  due_date: string | null;
  /** NULL on a term nobody bills through this system — see `scheduleSource`. */
  subtotal: number | null;
  sst: number | null;
  total: number | null;
  status: 'scheduled' | 'invoiced' | 'paid' | 'failed' | 'offline' | 'void';
  review_due: number;
  invoice_id: number | null;
  invoice_no: string | null;
  invoice_status: 'pending' | 'paid' | 'void' | null;
  invoice_due_date: string | null;
  /** This cycle's breakdown — computed server-side by the same function the
   *  invoice was built from, so the two cannot disagree. */
  lines: Array<{
    code: string; description: string;
    quantity: number; unit_amount: number; amount: number;
  }>;
  /**
   * The CYCLE DEAL for this period, if one has been filed.
   *
   * 'won' is the only thing that means the books are closed — the charge
   * `status` above is about money, and a client billed in advance is `paid`
   * for a month nobody has opened yet.
   *
   * `null` means WE DO NOT KNOW, not "not done": it is null for every cycle
   * that was already won before the backend began recording it, since a won
   * deal never moves stage again and so is never re-synced. Callers fall back
   * to the calendar there.
   */
  deal_status: 'active' | 'won' | 'lost' | 'ended' | null;
  deal_id: string | null;
  /**
   * What this cycle actually covers.
   *
   * Server-computed from the same cadence engine the invoice uses, and
   * deliberately NOT derived from `lines` above: that breakdown is withheld
   * whenever the money cannot be proved to add up, and whether we file an SST
   * return in a period is not a money question.
   */
  has_einvoice: boolean;
  has_sst: boolean;
  /** Arrears can bunch several reviews onto one cycle, so a COUNT. */
  review_count: number;
}

/**
 * An add-on on this plan: whether it is running, and what it costs either way.
 *
 * Priced server-side by re-deriving with the add-on switched on, so the figure
 * shown here is the figure that would be charged. `available: false` means the
 * pricing engine refused it — `unavailable_reason` carries its wording, which
 * already explains what would have to change (SST filing is 2-monthly, so a
 * quarterly-closing client has to move cadence first).
 */
/** Which cycle an add-on would start with, and which one pays for it. */
export interface AccountingAddonStart {
  work_cycle_no: number;
  work_period_start: string | null;
  work_period_end: string | null;
  /** Null when every remaining instalment is already invoiced. */
  bill_cycle_no: number | null;
  bill_due_date: string | null;
}

export type AccountingAddonKey = 'einvoice' | 'sst_submission' | 'account_review';

export interface AccountingAddon {
  key: AccountingAddonKey;
  label: string;
  blurb: string;
  active: boolean;
  available: boolean;
  /** Null when it is already running, or when nothing upcoming can take it. */
  starts_from: AccountingAddonStart | null;
  /** Per cycle. Null when the engine could not price it. */
  amount: number | null;
  /** Falls due every Nth cycle rather than every one. */
  every_n_cycles: number | null;
  unavailable_reason: string | null;
  /** Where the periods AFTER the first are billed — on our instalments, or by
   *  the account manager. Null when unavailable or already active. */
  billing_note: string | null;
  /** An unpaid first-period invoice an earlier click raised. The page reopens
   *  payment on it rather than offering to add the same thing again. */
  pending_invoice: AccountingAddonInvoice | null;
  /** The review's cadence while it runs. */
  frequency?: 'quarterly' | 'halfyearly' | 'yearly' | null;
  /** The review's cadence choices on Add, priced per review. */
  options?: { value: 'quarterly' | 'halfyearly' | 'yearly'; label: string; amount: number }[] | null;
  /** Running: the end of the period already paid for — when a stop takes effect.
   *  Stopping: the day the cover they have paid for runs out. */
  ends_after?: string | null;
  /** Account Review only: switched off, but still inside the period already
   *  paid for. Not running, not gone — and free to turn back on. */
  stopping?: boolean;
  /** Running Account Review only: the windows its next reviews cover, soonest
   *  first — server-computed from the term's review clock. */
  upcoming_reviews?: AccountingReviewWindow[];
}

/** One Account Review: the books it covers, done once the window has closed. */
export interface AccountingReviewWindow {
  review_no: number;
  period_start: string;
  period_end: string;
  /** in_progress: window closed, CS's review deal still open.
   *  current: the window is running. upcoming: not started yet. */
  state: 'in_progress' | 'current' | 'upcoming';
}

/** The first-period invoice the Add button raises. Paid like any other. */
export interface AccountingAddonInvoice {
  id: number;
  total: number;
  currency: string;
  due_date: string | null;
  /** A transfer was declared on it and CS is confirming — no Pay, no Cancel. */
  under_review?: boolean;
}

/** The contract row for a term — an immutable snapshot, unlike the selection. */
export interface AccountingTermRow {
  id: number;
  term_no: number;
  term_start: string;
  term_end: string | null;
  status: 'pending_payment' | 'active' | 'expiring' | 'renewed' | 'lapsed' | 'cancelled';
  plan: AccountingPlan;
  tier: DiyTier | null;
  closing_frequency: ClosingFrequency | null;
  billing_interval_months: number;
  cycles_per_term: number;
  term_total: number | null;
  /** The agreed RECURRING price, snapshotted at activation — what every cycle
   *  costs once cycle 1's one-offs are behind them. */
  plan_period_amount: number | null;
  account_review_period_amount: number | null;
  einvoice_monthly_amount: number | null;
}

const STORAGE_PREFIX = 'altomate.accounting.plan_selection.';

@Injectable({ providedIn: 'root' })
export class AccountingActivationService {
  /** Last server-derived pricing for the current company. */
  private readonly _pricing = signal<AccountingPricingResult | null>(null);
  readonly pricing = this._pricing.asReadonly();

  private readonly _invoice = signal<AccountingInvoiceRef | null>(null);
  readonly invoice = this._invoice.asReadonly();

  /** The latest term and its instalment ledger, from the same call as the
   *  selection. Empty until `loadSelection` has run for this company. */
  private readonly _term = signal<AccountingTermRow | null>(null);
  readonly term = this._term.asReadonly();

  private readonly _schedule = signal<AccountingCharge[]>([]);
  readonly schedule = this._schedule.asReadonly();

  /**
   * Where the schedule came from, which decides how much of it to believe.
   *
   *   'charges' — the real instalment ledger: dates, money, invoices
   *   'derived' — closing dates only, for an offline engagement billed by an
   *               account manager outside this system. No amounts.
   *   'none'    — no term, or nothing could be worked out
   */
  private readonly _scheduleSource = signal<'charges' | 'derived' | 'none'>('none');
  readonly scheduleSource = this._scheduleSource.asReadonly();

  /** Add-ons available on the current plan, with their on/off state. Empty on
   *  a plan that has none, and on a negotiated one where adding is a CS job. */
  private readonly _addons = signal<AccountingAddon[]>([]);
  readonly addons = this._addons.asReadonly();

  /** An Account Review paid for and not yet delivered, on a plan that has no
   *  add-on row to show it — a DIY client who upgraded to bookkeeping. */
  private readonly _reviewOwed = signal<string | null>(null);
  readonly reviewOwed = this._reviewOwed.asReadonly();

  readonly requiresConsultation = computed(
    () => this._pricing()?.requires_consultation === true
  );

  /** Server-computed entitlement for the current company. */
  private readonly _summary = signal<AccountingSummary | null>(null);
  readonly summary = this._summary.asReadonly();

  private readonly _hasLoaded = signal<boolean>(false);
  readonly hasLoaded = this._hasLoaded.asReadonly();

  readonly isActive = computed(() => this._summary()?.activation_status === 'active');

  /**
   * Has this company ever been activated — the ACCESS question.
   *
   * Distinct from `isActive`, which is the BILLING question. An expired or
   * suspended company still owns its books and must keep the navigation to
   * reach them; what it may POST is enforced server-side by date. Gating the
   * top bar on `isActive` left a client who had passed the soft block on a
   * page with no menu, which is a wall by another route.
   *
   * Prefers the server's `can_enter`; the local list is the fallback for a
   * cached summary from before that field existed.
   */
  readonly canEnter = computed(() => {
    const sm = this._summary();
    if (!sm) return false;
    return sm.can_enter
      ?? ['active', 'suspended', 'expired'].includes(sm.activation_status);
  });

  /** Last date a record may be dated. null = no ceiling. */
  readonly coverageUntil = computed(() => this._summary()?.coverage_until ?? null);

  /** Term ended and not renewed — a prompt, never a block. */
  readonly renewalDue = computed(() =>
    this._summary()?.renewal_due ?? this._summary()?.activation_status === 'expired');
  /** Waiting on CS. Must suppress every "pay now" surface. */
  readonly isUnderReview = computed(() => this._summary()?.manual_review === true);
  readonly plan = computed(() => this._summary()?.plan ?? null);
  /** Bitrix tier code ('6328' free / '6329' paid), null unless active. */
  readonly bitrixPlanCode = computed(() =>
    this.isActive() ? (this._summary()?.bitrix_plan_code ?? null) : null);
  /** Bitrix service type ('6213' / '6324' / '2361'), null unless active. */
  readonly bitrixServiceType = computed(() =>
    this.isActive() ? (this._summary()?.bitrix_service_type ?? null) : null);

  /**
   * Tier, straight from SQL — prefer these over the Bitrix codes in NEW code.
   *
   * Validity is decided by `activation_status`, never by a CRM value. The Bitrix
   * codes above exist because six template branches and two components already
   * speak that vocabulary (and CS does too); they are a LABEL for the tier, not
   * the source of it. Anything new should read the tier directly.
   */
  /**
   * WHAT they bought, not whether the last invoice cleared.
   *
   * Deliberately keyed on `canEnter`, not `isActive` — same reasoning as
   * `canEnter` itself: an expired or suspended company still owns its books,
   * and the read-only reports (FX Gain/Loss, Journal, Aging, Fixed Asset) are
   * exactly what it must keep reaching. Gating those on `isActive` meant a
   * paying client whose instalment bounced was let INTO the module by the
   * guard and then found half the menu gone.
   *
   * Surfaces that WRITE must not use this on its own — see `canSubmitEInvoice`.
   */
  readonly isPaidTier = computed(() => {
    const sm = this._summary();
    if (!sm || !this.canEnter()) return false;
    return sm.plan === 'bookkeeping' || sm.tier === 'standard';
  });
  readonly isFreeTier = computed(() => {
    const sm = this._summary();
    if (!sm || sm.activation_status !== 'active') return false;
    return sm.plan === 'diy' && sm.tier === 'free';
  });
  /**
   * Paid AND in good standing. The write counterpart to `isPaidTier`: submitting
   * an e-invoice to LHDN is a billable action, so a lapsed or suspended client
   * keeps the reports but not the submission.
   */
  readonly canSubmitEInvoice = computed(() => this.isPaidTier() && this.isActive());
  readonly activationStatus = computed(() => this._summary()?.activation_status ?? 'none');
  readonly isBookkeeping = computed(() => this.plan() === 'bookkeeping');

  /**
   * Which package the client's books actually live in, or null.
   *
   * ONE definition, because the NULL rule is subtle enough that a second copy
   * would drift from it. NULL on a bookkeeping row means ALTOMATE: the column
   * was added after the fact and third-party activation did not exist before
   * it, so every row predating it is an Altomate client by construction — the
   * same reasoning as scripts/backfillAccountingSoftware.sql, and true whether
   * or not that script has been run.
   */
  readonly accountingSoftware = computed<string | null>(() => {
    const raw = this._summary()?.selection?.accounting_software;
    return raw ? String(raw) : null;
  });

  /**
   * A bookkeeping client whose books are kept in someone else's package.
   *
   * They have no Altomate accounting module — there is no ledger here to show
   * them — so the module's navigation is hidden and the accounting area
   * resolves to their engagement page alone. DIY is never this: DIY *is*
   * Altomate Accounting.
   */
  readonly usesThirdPartySoftware = computed(() => {
    // Only once they are a client. A draft (chose a plan, left at payment) has
    // not bought anything yet — it belongs on /accounting/home, which offers
    // Resume payment and Change plan. Redirecting it to the engagement page
    // stranded it on "Not started" with no way back into the wizard.
    if (!this.canEnter()) return false;
    if (!this.isBookkeeping()) return false;
    const key = this.accountingSoftware();
    return !!key && key !== 'altomate';
  });
  readonly isDiy = computed(() => this.plan() === 'diy');

  /** Per-company cache + in-flight dedupe, so N components asking cost one call. */
  private readonly cache: Record<string, AccountingSummary> = {};
  private readonly inflight: Record<string, Promise<AccountingSummary | null>> = {};

  /** In-flight guard so a double-click cannot post twice. */
  private saving = false;

  private readonly state = inject(StateService);

  constructor(private http: HttpClient) {}

  // ---------------------------------------------------------------- entitlement
  /**
   * Load (or return cached) entitlement for a company.
   *
   * Failure resolves to the last good summary rather than null: a flaky network
   * must not read as "not entitled" and lock a paying customer out.
   */
  async load(companyId: string | number, force = false): Promise<AccountingSummary | null> {
    const key = String(companyId || '');
    if (!key) return null;

    if (!force && this.cache[key]) {
      // This function is called from effect()s (app shell, settings, bank statement) on every
      // organisation switch. A signal write inside an effect throws NG0600 on Angular 18, and
      // this branch used to write before its first await, so it threw whenever the company's
      // entitlement was already cached. Yielding one microtask moves the writes out of the
      // caller's reactive context; callers that await the result still see the summary set.
      await Promise.resolve();
      this._summary.set(this.cache[key]);
      this._hasLoaded.set(true);
      return this.cache[key];
    }
    if (this.inflight[key]) return this.inflight[key];

    const req = firstValueFrom(
      this.http.get<AccountingSummary & { success: boolean }>(
        `${accountingUrl}/accounting/dashboard/${key}`
      )
    )
      .then((res) => {
        if (!res?.success) return this.cache[key] ?? null;
        this.cache[key] = res;
        this._summary.set(res);
        this._hasLoaded.set(true);
        return res;
      })
      .catch(() => this.cache[key] ?? null)
      .finally(() => { delete this.inflight[key]; });

    this.inflight[key] = req;
    return req;
  }

  /** Drop the cached summary so the next load hits the server. */
  invalidate(companyId: string | number) {
    delete this.cache[String(companyId || '')];
  }

  /**
   * Where this company belongs, derived from server state only.
   *
   * An abandoned draft resolves to `home`, never pinning a user inside the
   * wizard — same rule as payroll's nextStage().
   */
  nextStage(summary: AccountingSummary | null): AccountingStage {
    if (!summary) return 'home';

    if (summary.activation_status === 'active') {
      return summary.setup_steps?.accounting_profile === false ? 'profile' : 'dashboard';
    }
    // Paid — never send them back to the wizard.
    if (summary.payment_status === 'paid') return 'dashboard';
    // Submitted and waiting on CS. Routed HOME, not to payment: the invoice is
    // still pending, but re-opening a payment form for someone who has already
    // transferred is how you get paid twice.
    if (summary.manual_review) return 'home';
    // Chose a paid plan, invoice still outstanding.
    if (summary.activation_status === 'draft') return 'payment';
    return 'home';
  }

  stageToUrl(stage: AccountingStage): string {
    switch (stage) {
      case 'plan': return '/accounting/activate/plan';
      case 'payment': return '/accounting/activate/payment';
      case 'profile': return '/accounting/setting';
      case 'dashboard': return '/accounting/home';
      default: return '/accounting/home';
    }
  }

  // ------------------------------------------------------------------ storage
  /**
   * Cached snapshot so the configurator can repaint instantly on re-entry.
   * Display only — the server re-derives on every save.
   */
  readSnapshot(companyId: string | number): AccountingPricingResult | null {
    try {
      const raw = localStorage.getItem(STORAGE_PREFIX + companyId);
      return raw ? JSON.parse(raw) as AccountingPricingResult : null;
    } catch {
      return null;
    }
  }

  private writeSnapshot(companyId: string | number, value: AccountingPricingResult | null) {
    try {
      const key = STORAGE_PREFIX + companyId;
      if (value) localStorage.setItem(key, JSON.stringify(value));
      else localStorage.removeItem(key);
    } catch {
      /* private browsing / quota — the server copy is authoritative anyway */
    }
  }

  clearSnapshot(companyId: string | number) {
    this.writeSnapshot(companyId, null);
    this._pricing.set(null);
    this._invoice.set(null);
    this._term.set(null);
    this._schedule.set([]);
    this._scheduleSource.set('none');
    this._addons.set([]);
    this._reviewOwed.set(null);
  }

  // ---------------------------------------------------------------- server ops
  /** One package on the software question, as GET /accounting/pricing lists it. */
  async loadSoftwareChoices(): Promise<AccountingSoftwareChoice[]> {
    const res = await firstValueFrom(
      this.http.get<{ success: boolean; pricing?: { accounting_software?: AccountingSoftwareChoice[] } }>(
        `${accountingUrl}/accounting/pricing`)
    ).catch(() => null);
    return Array.isArray(res?.pricing?.accounting_software) ? res!.pricing!.accounting_software! : [];
  }

  /**
   * Price a selection WITHOUT persisting it — used to keep the displayed total
   * honest while the user is still choosing. The response is the same shape the
   * save returns, so the summary never has to compute money itself.
   */
  async price(
    companyId: string | number,
    keys: AccountingSelectionKeys
  ): Promise<AccountingPricingResult | null> {
    const res = await firstValueFrom(
      this.http.post<SaveResponse>(`${accountingUrl}/accounting/plan-selection/preview`, {
        userID: companyId, ...keys
      })
    ).catch(() => null);

    if (!res?.success || !res.selection) return null;
    this._pricing.set(res.selection);
    return res.selection;
  }

  /** Rehydrate a persisted draft so a refresh does not lose the selection. */
  async loadSelection(companyId: string | number): Promise<AccountingPricingResult | null> {
    const res = await firstValueFrom(
      this.http.get<{
        success: boolean; selection: any; derived: AccountingPricingResult | null;
        pricing_source?: 'matrix' | 'custom';
        term?: AccountingTermRow | null;
        schedule?: AccountingCharge[];
        schedule_source?: 'charges' | 'derived' | 'none';
        addons?: AccountingAddon[];
        review_owed?: string | null;
        latest_invoice: AccountingInvoiceRef | null;
      }>(
        `${accountingUrl}/accounting/plan-selection/${companyId}`
      )
    ).catch(() => null);

    if (!res?.success || !res.selection) {
      // A company with no selection row has no schedule either — clear both
      // rather than leaving the previous company's term on screen. The
      // add-ons are kept: a LEGACY activation (no selection row) still gets
      // its list from the server, built from the activation (v2.32).
      this._term.set(null);
      this._schedule.set([]);
      this._scheduleSource.set('none');
      this._addons.set(Array.isArray(res?.addons) ? res.addons : []);
      this._reviewOwed.set(null);
      return null;
    }
    this._invoice.set(res.latest_invoice);
    // `?? null` / `?? []` rather than `!` — an older backend answers without
    // these keys, and a page reading them must see "none", not undefined.
    this._term.set(res.term ?? null);
    this._schedule.set(Array.isArray(res.schedule) ? res.schedule : []);
    this._scheduleSource.set(res.schedule_source || 'none');
    this._addons.set(Array.isArray(res.addons) ? res.addons : []);
    this._reviewOwed.set(res.review_owed ?? null);

    // Prefer the server's DERIVED object. The stored row carries the client's
    // answers and the amounts but no breakdown — no `lines`, no
    // `cycles_per_term`, no `term_total` — so returning it left the invoice
    // card with an empty line-item list and a 12-month term described as
    // "billed monthly, cancel anytime".
    //
    // Falling back to the row keeps the page rendering against an older
    // backend rather than showing nothing.
    if (!res.derived) return res.selection as AccountingPricingResult;
    return {
      ...(res.selection as AccountingPricingResult),
      ...res.derived,
      pricing_source: res.pricing_source || 'matrix'
    };
  }

  /**
   * Add an add-on to a live plan — a real change, not an enquiry.
   *
   * Raises the invoice for the add-on's FIRST period and returns it; the add-on
   * itself goes live when that invoice is paid (card, or a bank transfer CS
   * verifies). The server decides which cycle it starts with, prices the
   * period, and — on settlement — re-prices later instalments and raises the
   * delivery task. Nothing is priced or scheduled here.
   */
  async addAddon(
    companyId: string | number,
    addon: AccountingAddonKey,
    opts: { account_review_freq?: 'quarterly' | 'halfyearly' | 'yearly' } = {}
  ): Promise<{
    ok: boolean; message?: string; code?: string;
    invoice?: AccountingAddonInvoice; existing?: boolean; start?: AccountingAddonStart;
    /** Turned back on inside the period already paid for — no invoice. */
    resumed?: boolean; endsAfter?: string | null;
  }> {
    const res = await firstValueFrom(
      this.http.post<{
        success: boolean; message?: string; code?: string; existing?: boolean;
        invoice?: AccountingAddonInvoice; resumed?: boolean; ends_after?: string | null;
        starts_from?: { work_cycle_no: number; work_period_start: string | null;
          work_period_end: string | null } | null;
      }>(`${accountingUrl}/accounting/plan-selection/addon`, { userID: companyId, addon, ...opts })
    ).catch((e) => e?.error || null);

    // A resume changes the plan there and then and raises nothing to pay.
    if (res?.success && res.resumed) {
      this.invalidate(companyId);
      return { ok: true, resumed: true, endsAfter: res.ends_after ?? null };
    }
    if (!res?.success || !res.invoice) {
      return { ok: false, message: res?.message, code: res?.code };
    }
    // Nothing on the plan has changed yet — that happens when the invoice is
    // paid — but the add-on list now carries a pending invoice, so refresh it.
    this.invalidate(companyId);
    return {
      ok: true,
      invoice: res.invoice,
      existing: res.existing === true,
      start: res.starts_from ? {
        work_cycle_no: res.starts_from.work_cycle_no,
        work_period_start: res.starts_from.work_period_start ?? null,
        work_period_end: res.starts_from.work_period_end ?? null,
        bill_cycle_no: null,
        bill_due_date: null
      } : undefined
    };
  }

  // -------------------------------------------------------------- plan change
  /**
   * DIY Standard → DIY Free. Writes ONE pending row on the server; nothing on
   * the live plan changes until the paid period ends. The response carries the
   * effective date for the confirmation, and whether a review was kept.
   */
  async requestDowngrade(
    companyId: string | number,
    keepReview: boolean
  ): Promise<{ ok: boolean; code?: string; message?: string;
    change?: { effective_at: string; keep_review: boolean; has_review: boolean } }> {
    const res = await firstValueFrom(
      this.http.post<{ success: boolean; code?: string; message?: string;
        change?: { effective_at: string; keep_review: boolean; has_review: boolean } }>(
        `${accountingUrl}/accounting/plan-change/downgrade`,
        { userID: companyId, keep_review: keepReview })
    ).catch((e) => e?.error || null);
    if (!res?.success) return { ok: false, code: res?.code, message: res?.message };
    this.invalidate(companyId);
    return { ok: true, change: res.change };
  }

  /**
   * DIY Free → DIY Standard. Writes the pending row AND mints the first month's
   * invoice; the plan changes when that invoice is paid. A second call while
   * it is unpaid returns the same invoice (`existing`).
   */
  async requestUpgradeStandard(
    companyId: string | number,
    setupEnabled: boolean
  ): Promise<{ ok: boolean; code?: string; message?: string;
    invoice?: AccountingAddonInvoice; existing?: boolean }> {
    const res = await firstValueFrom(
      this.http.post<{ success: boolean; code?: string; message?: string;
        existing?: boolean; invoice?: AccountingAddonInvoice }>(
        `${accountingUrl}/accounting/plan-change/upgrade-standard`,
        { userID: companyId, setup_enabled: setupEnabled })
    ).catch((e) => e?.error || null);
    if (!res?.success || !res.invoice) return { ok: false, code: res?.code, message: res?.message };
    this.invalidate(companyId);
    return { ok: true, invoice: res.invoice, existing: !!res.existing };
  }

  /** Switch a running add-on off. It runs to the end of the paid period. */
  async stopAddon(
    companyId: string | number,
    addon: AccountingAddonKey
  ): Promise<{ ok: boolean; message?: string; ends_after?: string | null }> {
    const res = await firstValueFrom(
      this.http.post<{ success: boolean; message?: string; ends_after?: string | null }>(
        `${accountingUrl}/accounting/plan-selection/addon/stop`, { userID: companyId, addon })
    ).catch((e) => e?.error || null);
    if (res?.success) this.invalidate(companyId);
    return { ok: res?.success === true, message: res?.message, ends_after: res?.ends_after ?? null };
  }

  /** Withdraw an unpaid add-on request — its invoice is voided. */
  async cancelAddon(
    companyId: string | number,
    addon: AccountingAddonKey
  ): Promise<{ ok: boolean; message?: string }> {
    const res = await firstValueFrom(
      this.http.post<{ success: boolean; message?: string }>(
        `${accountingUrl}/accounting/plan-selection/addon/cancel`, { userID: companyId, addon })
    ).catch((e) => e?.error || null);
    if (res?.success) this.invalidate(companyId);
    return { ok: res?.success === true, message: res?.message };
  }

  /** Cancel the pending change. A downgrade is a status flip; an upgrade also
   *  voids its unpaid invoice. */
  async cancelPlanChange(companyId: string | number): Promise<{ ok: boolean; message?: string }> {
    const res = await firstValueFrom(
      this.http.post<{ success: boolean; message?: string }>(
        `${accountingUrl}/accounting/plan-change/cancel`, { userID: companyId })
    ).catch((e) => e?.error || null);
    if (res?.success) this.invalidate(companyId);
    return { ok: res?.success === true, message: res?.message };
  }

  // ------------------------------------------------------------- consultation
  /**
   * Record a request to talk to an advisor.
   *
   * Carries whatever the client had configured, so CS starts from what they
   * already worked out rather than from nothing. Available to every company —
   * being inside the published turnover bands does not mean you have no
   * questions.
   */
  async requestConsultation(
    companyId: string | number,
    payload: {
      source: 'service_cards' | 'configurator' | 'turnover_band' | 'no_cadence_fits'
        | 'my_plan';
      /**
       * WHAT the enquiry is about. Decides the lead's TITLE, which is the only
       * thing CS sees in a queue — without it every request reads "Altomate
       * Accounting consultation" and a cancellation looks like a question.
       * Keys must match CONSULTATION_TOPICS in accountingBitrix.js; an
       * unrecognised one is dropped and the lead gets the generic title.
       */
      topic?: 'upgrade_standard' | 'downgrade_free' | 'add_bookkeeping'
        | 'move_to_altomate' | 'adhoc_services'
        | 'add_einvoice' | 'add_sst_submission';
      plan?: AccountingPlan | null;
      closing_frequency?: ClosingFrequency | null;
      turnover_band?: string | null;
      term_start?: string | null;
      term_end?: string | null;
      addon_account_review?: boolean;
      addon_einvoice?: boolean;
      note?: string | null;
      // Quote-request detail. Only present when the request came through the
      // quote form; a plain "talk to us" click still sends none of it.
      year_end?: number | null;
      incorporation_date?: string | null;
      last_submission_url?: string | null;
      last_submission_name?: string | null;
      /**
       * Ad hoc services from the published fee schedule the client wants
       * discussed. Keys only — every one of these is quoted as a range, so no
       * amount travels with them and none is charged until CS quotes it.
       */
      adhoc_services?: string[];
      /** A DIY client asking for a bookkeeping quote from the wizard in
       *  upgrade mode — the server files the pending change row first. */
      upgrade?: boolean;
    }
  ): Promise<boolean> {
    // The CRM contact of whoever is signed in, so the lead names a person CS
    // can ring rather than only the company. Populated by /me at app start; a
    // hard reload mid-flow can beat that call, hence the null-safe read — a
    // missing contact must not stop the request being filed.
    const contactId = this.state.userInfo?.contactId ?? null;

    const res = await firstValueFrom(
      this.http.post<{ success: boolean }>(
        `${accountingUrl}/accounting/consultation`,
        { userID: companyId, contactId, ...payload }
      )
    ).catch(() => null);
    return res?.success === true;
  }

  // ------------------------------------------------------------------ payment
  /**
   * Mint a one-time PaymentIntent for the company's open activation invoice.
   *
   * No amount is sent — the server reads it back off the invoice. Passing one
   * would be the obvious place for a tampered total to slip in.
   */
  async createPaymentIntent(companyId: string | number): Promise<AccountingIntent | null> {
    return firstValueFrom(
      this.http.post<AccountingIntent>(`${accountingUrl}/accounting/payment-intent`, {
        userID: companyId, country: 'MY'
      })
    ).catch(() => null);
  }

  /**
   * Mint a one-time PaymentIntent for whatever the company currently owes —
   * an overdue cycle instalment (the suspended screen, the engagement page)
   * or, before activation, the same initial invoice createPaymentIntent
   * covers. Same no-amount-from-client rule applies.
   */
  async createDuePaymentIntent(
    companyId: string | number, invoiceId: number | null = null
  ): Promise<AccountingIntent | null> {
    return firstValueFrom(
      this.http.post<AccountingIntent>(`${accountingUrl}/accounting/payment-intent/due`, {
        userID: companyId, country: 'MY',
        // A NAMED invoice (an add-on's) rather than whichever bill is due.
        ...(invoiceId ? { invoiceId } : {})
      })
    ).catch(() => null);
  }

  /**
   * Card-on-file. When there is something to pay the server returns an intent
   * tied to the customer, so ONE drop-in both charges instalment 1 and
   * registers the mandate for the rest of the term.
   */
  async createRecurringConsent(companyId: string | number): Promise<AccountingIntent | null> {
    return firstValueFrom(
      this.http.post<AccountingIntent>(`${accountingUrl}/accounting/recurring/consent`, {
        userID: companyId, country: 'MY'
      })
    ).catch(() => null);
  }

  /** Persist the mandate after the drop-in's success event. Best-effort: a
   *  failure here must not block the payment that already went through. */
  async saveRecurringConsent(
    companyId: string | number,
    payload: { customerId: string; consentId?: string | null; paymentMethodId?: string | null; email?: string | null }
  ): Promise<boolean> {
    const res = await firstValueFrom(
      this.http.post<{ success: boolean }>(
        `${accountingUrl}/accounting/recurring/consent/save`,
        { userID: companyId, ...payload }
      )
    ).catch(() => null);
    return res?.success === true;
  }

  /**
   * Ask the server to check with Airwallex whether the open invoice was in fact
   * already paid, and settle it if so.
   *
   * Covers the cases the browser cannot: a redirect payment method that
   * navigated the page away mid-handler, a closed tab, or a webhook rejected
   * for a bad signature. Safe to call on every visit.
   */
  async reconcilePayment(
    companyId: string | number
  ): Promise<{ settled: boolean; reason: string | null; reachable: boolean }> {
    // Distinguish "we asked and there is no payment" from "we could not ask".
    // Collapsing those into a single false told a customer who HAD paid that no
    // payment existed — the most alarming possible reading of a 404.
    const res = await firstValueFrom(
      this.http.post<{ success: boolean; settled: boolean; reason?: string; status?: string }>(
        `${accountingUrl}/accounting/payment-intent/reconcile`,
        { userID: companyId }
      )
    ).catch(() => null);

    if (!res) return { settled: false, reason: null, reachable: false };
    if (res.settled) this.invalidate(companyId);
    return {
      settled: res.settled === true,
      reason: res.reason || res.status || null,
      reachable: true
    };
  }

  /**
   * Same check as reconcilePayment(), but against whatever is currently DUE
   * (a cycle instalment) rather than always the activation invoice.
   *
   * reconcilePayment() resolves the server's activation invoice, which for an
   * already-active company is long since paid — asking it "was this settled?"
   * about an overdue cycle instalment answered about the WRONG invoice and
   * reported it paid. This hits /accounting/payment-intent/due/reconcile,
   * the sibling that resolves the same invoice createDuePaymentIntent() mints
   * against.
   */
  async reconcileDuePayment(
    companyId: string | number, invoiceId: number | null = null
  ): Promise<{ settled: boolean; reason: string | null; reachable: boolean }> {
    const res = await firstValueFrom(
      this.http.post<{ success: boolean; settled: boolean; reason?: string; status?: string }>(
        `${accountingUrl}/accounting/payment-intent/due/reconcile`,
        { userID: companyId, ...(invoiceId ? { invoiceId } : {}) }
      )
    ).catch(() => null);

    if (!res) return { settled: false, reason: null, reachable: false };
    if (res.settled) this.invalidate(companyId);
    return {
      settled: res.settled === true,
      reason: res.reason || res.status || null,
      reachable: true
    };
  }

  /**
   * Settle the activation invoice: opens term 1 and flips the company active.
   * The Airwallex webhook does the same thing independently, so this being slow
   * or failing does not strand a paid customer.
   */
  async payInvoice(
    companyId: string | number,
    invoiceId: number,
    method: 'card' | 'manual' = 'card'
  ): Promise<boolean> {
    const res = await firstValueFrom(
      this.http.post<{ success: boolean }>(
        `${accountingUrl}/accounting/invoices/${invoiceId}/pay`,
        { userID: companyId, method }
      )
    ).catch(() => null);
    if (res?.success) this.invalidate(companyId);
    return res?.success === true;
  }

  /**
   * Bank transfer or existing-client declaration. Records it; does NOT
   * activate — CS confirms first, exactly as payroll does.
   *
   * `offline` marks an existing Altomate client: the published price does not
   * apply to them, so the invoice is zeroed and their billing stays with their
   * account manager.
   */
  async submitManualPayment(
    companyId: string | number,
    receiptUrl: string | null,
    offline = false,
    invoiceId: number | null = null
  ): Promise<AccountingSaveResult> {
    const res = await firstValueFrom(
      this.http.post<{ success: boolean; message?: string }>(
        `${accountingUrl}/accounting/activation/pay-manual`,
        {
          userID: companyId,
          receiptUrl: receiptUrl || null,
          ...(offline ? { billingMode: 'offline' } : {}),
          ...(invoiceId ? { invoiceId } : {})
        }
      )
    ).catch((err) => ({ success: false, message: err?.error?.message }));
    if (res?.success) this.invalidate(companyId);
    return { ok: res?.success === true, message: res?.message };
  }

  /**
   * Persist the selection. Returns the server-derived pricing — the caller must
   * render THAT, not its own arithmetic.
   */
  /**
   * @param renewal Buying the NEXT term rather than the first. The server does
   *   not take the client's word for it — it checks a_subscription_term — but
   *   without the flag a renewal is refused as `already_committed`.
   */
  async savePlanSelection(
    companyId: string | number,
    keys: AccountingSelectionKeys,
    renewal = false,
    /** An active DIY client moving up to bookkeeping: the server writes a
     *  pending change row and mints the invoice, and leaves the live plan alone. */
    upgrade = false
  ): Promise<AccountingSaveResult> {
    if (this.saving) return { ok: false, message: 'Save already in progress.' };
    this.saving = true;

    try {
      const res = await firstValueFrom(
        this.http.post<SaveResponse>(`${accountingUrl}/accounting/plan-selection`, {
          userID: companyId, ...keys, renewal, upgrade
        })
      );

      if (!res?.success || !res.selection) {
        return { ok: false, message: res?.message || 'Could not save your plan selection.', code: res?.code };
      }

      this._pricing.set(res.selection);
      this._invoice.set(res.invoice ?? null);
      this.writeSnapshot(companyId, res.selection);

      return { ok: true, pricing: res.selection, invoice: res.invoice ?? null };
    } catch (err: any) {
      const body = err?.error;
      return {
        ok: false,
        message: body?.message || 'Could not save your plan selection.',
        code: body?.code
      };
    } finally {
      this.saving = false;
    }
  }
}
